import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Outlet, useLocation, useNavigate } from "react-router-dom";

import { useMediaQuery, useTheme } from "@mui/material";
import {
  AppNav,
  DrawerHeaderNav,
  Footer,
  HeaderNav,
  iconRegistry,
  Theme,
  UNEPLogo,
} from "@wesr/wesr-design-system";
import { WelcomeModal } from "../../components";

import { IAppNavItems, IFooterNavItems, IMainNavItems } from "@wesr/wesr-common";
import { loadAppNavConfig, loadFooterConfig, loadNavigationConfig } from "../../utils/Navigation";
import { WESRContainer, Wrapper } from "./PublicLayout.syles";

const PublicLayout: FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const [modalOpen, setModalOpen] = useState<boolean>(() => {
    // Check localStorage on initial render
    const isHomepage = location.pathname === "/";
    if (!isHomepage) return false;
    const dismissed = localStorage.getItem("poc-modal-dismissed");
    return dismissed !== "true"; // Show modal if not dismissed
  });

  const showDrawerNav = useMediaQuery(theme.breakpoints.down(Theme.breakpoints.xl));

  const [appNavConfig, setAppNavConfig] = useState<IAppNavItems[]>([]);
  const [navigationConfig, setNavigationConfig] = useState<IMainNavItems[]>([]);
  const [footerItems, setFooterItems] = useState<IFooterNavItems[]>([]);

  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadNav = async () => {
      try {
        const appNav = await loadAppNavConfig();
        setAppNavConfig(appNav);
      } catch (error) {
        console.error("[nav] Failed to load app navigation", error);
      }

      try {
        const mainNav = await loadNavigationConfig();
        setNavigationConfig(mainNav);
      } catch (error) {
        console.error("[nav] Failed to load main navigation", error);
      }

      try {
        const footerNav = await loadFooterConfig();
        setFooterItems(
          footerNav.items.map(item => ({
            label: t(item.label),
            url: item.url,
          })),
        );
      } catch (error) {
        console.error("[nav] Failed to load footer navigation", error);
      }

      setIsLoading(false);
    };
    loadNav();
  }, []);

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const handleAccept = () => {
    localStorage.setItem("poc-modal-dismissed", "true");
    setModalOpen(false);
  };

  if (isLoading) {
    return <div></div>; // or skeleton
  }

  return (
    <>
      <WelcomeModal isOpen={modalOpen} handleClose={handleCloseModal} handleAccept={handleAccept} />
      <AppNav
        appNavItems={appNavConfig.map(item => ({ label: t(`${item.label}`), url: item.url }))}
      />
      {!showDrawerNav && (
        <HeaderNav
          navigationItems={navigationConfig.map(item => {
            const IconComponent = iconRegistry[item.icon];

            return {
              label: t(item.title),
              to: item.to,
              icon: <IconComponent />,
              isInPOC: item.isInPOC,
              isActive: location.pathname === `/${item.to}`,
              sublabel: !item.isInPOC ? t("common.comingSoon") : undefined,
            };
          })}
          isHomepage={location.pathname === "/"}
          bgColour={Theme.palette.primary.darkGrey}
          colour="#FFFFFF"
          headerLogo={<UNEPLogo />}
          logoUrl={"/"}
        />
      )}

      {showDrawerNav && (
        <DrawerHeaderNav
          navigationItems={navigationConfig.map(item => {
            const IconComponent = iconRegistry[item.icon];

            return {
              label: t(item.title),
              to: item.to,
              icon: <IconComponent />,
              isInPOC: item.isInPOC,
              sublabel: !item.isInPOC ? t("common.comingSoon") : undefined,
            };
          })}
          headerLogo={<UNEPLogo />}
          logoUrl={"/"}
          hasDrawer={true}
          onNavigate={navigate}
          isTransparent={location.pathname === "/"}
          type="secondary"
          hideOnScroll={true}
          transparentWhenTop={true}
        />
      )}
      <Wrapper>
        <WESRContainer>
          <Outlet />
        </WESRContainer>
        <Footer footerItems={footerItems ?? []} footerSocialMedia={[]} />
      </Wrapper>
    </>
  );
};

export default PublicLayout;
